import { useMemo } from "react";
import type { EFFeatureDefinition } from "@/features/runtime/FeatureDefinition";
import { FEATURE_CATALOG } from "@/features/runtime/FeatureCatalog";
import {
	createEFFeatureRegistry,
	getRequiredFeatureDefinition,
	resolveFeatureActivation,
	resolveFeatureEligibility,
} from "@/features/runtime/FeatureRegistry";
import type {
	EFFeatureActivationState,
	EFFeatureEligibilityState,
	EFFeatureId,
	EFFeatureRuntime,
} from "@/features/runtime/FeatureTypes";

export type UseFeatureRegistryResult = {
	definitions: readonly EFFeatureDefinition[];
	registry: ReadonlyMap<EFFeatureId, EFFeatureDefinition>;
	getFeatureDefinition: (featureId: EFFeatureId) => EFFeatureDefinition;
	resolveFeatureState: (
		featureId: EFFeatureId,
		runtime: EFFeatureRuntime
	) => {
		definition: EFFeatureDefinition;
		eligibility: EFFeatureEligibilityState;
		activation: EFFeatureActivationState;
	};
};

export function useFeatureRegistry(): UseFeatureRegistryResult {
	const definitions = useMemo<readonly EFFeatureDefinition[]>(
		() => FEATURE_CATALOG,
		[]
	);

	const registry = useMemo<ReadonlyMap<EFFeatureId, EFFeatureDefinition>>(
		() => createEFFeatureRegistry(definitions),
		[definitions]
	);

	const getFeatureDefinition = useMemo(
		() => (featureId: EFFeatureId): EFFeatureDefinition =>
			getRequiredFeatureDefinition(registry, featureId),
		[registry]
	);

	const resolveFeatureState = useMemo(
		() =>
			(featureId: EFFeatureId, runtime: EFFeatureRuntime) => {
				const definition = getRequiredFeatureDefinition(registry, featureId);
				const eligibility = resolveFeatureEligibility(definition, runtime);
				const activation = resolveFeatureActivation(
					definition,
					runtime,
					eligibility
				);

				return {
					definition,
					eligibility,
					activation,
				};
			},
		[registry]
	);

	return {
		definitions,
		registry,
		getFeatureDefinition,
		resolveFeatureState,
	};
}

export default useFeatureRegistry;